/**
 * Connection Monitor
 *
 * Browser Supabase realtime is disabled for server-side auth. This monitor uses
 * the app health endpoint as a lightweight API reachability signal.
 */

import { SubscriptionStatus } from "./types";

export interface ConnectionMonitorCallbacks {
  onStatusChange?: (status: SubscriptionStatus) => void;
}

export class ConnectionMonitor {
  private status: SubscriptionStatus = "disconnected";
  private checkInterval: NodeJS.Timeout | null = null;

  constructor(private callbacks: ConnectionMonitorCallbacks = {}) {}

  start(): void {
    this.checkConnection();
    this.checkInterval = setInterval(() => {
      this.checkConnection();
    }, 30_000);
  }

  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.setStatus("disconnected");
  }

  private setStatus(status: SubscriptionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.callbacks.onStatusChange?.(status);
    }
  }

  private async checkConnection(): Promise<void> {
    this.setStatus("connecting");
    try {
      const response = await fetch("/api/health", {
        method: "GET",
        cache: "no-store",
      });
      this.setStatus(response.ok ? "connected" : "error");
    } catch {
      this.setStatus("disconnected");
    }
  }

  getStatus(): SubscriptionStatus {
    return this.status;
  }
}
